<template>
  <div
    v-if="editor"
    class="kv-editor kv-markdown-input"
    :class="{ 'kv-disabled': disabled }"
    :style="{ '--editor-rows': rows }"
  >
    <editor-content :editor="editor" spellcheck="false" />
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import Vue from 'vue';
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import tippy from 'tippy.js';
import MentionList from "./mention-list.vue";

function parseMarkdownToHTML(markdown) {
  if (!markdown) return "";
  let html = markdown;
  
  html = html.replace(/@\[([^\]]+)\]\(([^)]+)\)/g, (match, label, id) => {
    return `<span data-type="mention" data-id="${id}" data-label="${label}"></span>`;
  });
  
  // Convert newlines to paragraphs
  html = html.split('\n').map(line => `<p>${line}</p>`).join('');
  
  return html;
}

export default {
  name: "KvMarkdownEditor",
  props: {
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    mentionItems: {
      type: Array,
      default: () => [],
    },
    rows: {
      type: Number,
      default: 3,
    }
  },
  components: {
    EditorContent,
  },
  data() {
    return {
      editor: null,
      mentionPopup: null,
      mentionComponent: null,
      lastEmittedValue: "",
    };
  },
  mounted() {
    this.editor = new Editor({
      extensions: [
        StarterKit,
        Placeholder.configure({
          placeholder: this.placeholder || "Escreva um comentário...",
          emptyEditorClass: 'is-editor-empty',
        }),
        Mention.configure({
          HTMLAttributes: {
            class: 'mention',
          },
          renderLabel: ({ options, node }) => {
            return `@${node.attrs.label || node.attrs.id}`
          },
          suggestion: {
            items: ({ query }) => {
              return this.mentionItems.filter(item => item.title.toLowerCase().includes(query.toLowerCase())).slice(0, 5);
            },
            render: () => {
              return {
                onStart: (props) => {
                  this.mentionComponent = new Vue({
                    render: h => h(MentionList, {
                      props: {
                        items: props.items,
                        command: props.command,
                      }
                    })
                  }).$mount();

                  this.mentionPopup = tippy('body', {
                    getReferenceClientRect: props.clientRect,
                    appendTo: () => document.body,
                    content: this.mentionComponent.$el,
                    showOnCreate: true,
                    interactive: true,
                    trigger: 'manual',
                    placement: 'bottom-start',
                  });
                },
                onUpdate: (props) => {
                  this.mentionComponent.$children[0].items = props.items;
                  this.mentionComponent.$children[0].command = props.command;
                  if (this.mentionPopup && this.mentionPopup[0]) {
                    this.mentionPopup[0].setProps({
                      getReferenceClientRect: props.clientRect,
                    });
                  }
                },
                onKeyDown: (props) => {
                  if (props.event.key === 'Escape') {
                    if (this.mentionPopup && this.mentionPopup[0]) {
                      this.mentionPopup[0].hide();
                    }
                    return true;
                  }
                  return this.mentionComponent.$children[0].onKeyDown(props);
                },
                onExit: () => {
                  if (this.mentionPopup && this.mentionPopup[0]) {
                    this.mentionPopup[0].destroy();
                  }
                  if (this.mentionComponent) {
                    this.mentionComponent.$destroy();
                  }
                },
              }
            },
          },
        }),
      ],
      content: parseMarkdownToHTML(this.value || ""),
      onUpdate: ({ editor }) => {
        // Here we extract the text but replace mentions with @[label](id)
        let customMarkdown = "";
        const json = editor.getJSON();
        
        // A simple recursive parser for the json doc to standard string + mentions
        const parseNode = (node) => {
            let text = "";
            if (node.type === 'text') {
                let nodeText = node.text;
                if (node.marks) {
                    node.marks.forEach(mark => {
                        if (mark.type === 'bold') nodeText = `**${nodeText}**`;
                        if (mark.type === 'italic') nodeText = `_${nodeText}_`;
                        if (mark.type === 'strike') nodeText = `~${nodeText}~`;
                    });
                }
                text += nodeText;
            } else if (node.type === 'mention') {
                text += `@[${node.attrs.label}](${node.attrs.id})`;
            } else if (node.type === 'paragraph') {
                if (node.content) {
                    node.content.forEach(child => {
                        text += parseNode(child);
                    });
                }
                text += '\n'; // Add line break after paragraph
            } else if (node.content) {
                node.content.forEach(child => {
                    text += parseNode(child);
                });
            }
            return text;
        };
        
        customMarkdown = parseNode(json).trim();
        this.lastEmittedValue = customMarkdown;
        this.$emit("input", customMarkdown);
      },
    });
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
  watch: {
    value(newValue) {
      if (!this.editor) return;
      if (newValue === "" && !this.editor.isEmpty) {
        this.editor.commands.setContent("");
      } else if (newValue !== "" && newValue !== this.lastEmittedValue) {
        this.editor.commands.setContent(parseMarkdownToHTML(newValue));
      }
    }
  }
};
</script>

<style lang="less">
.kv-editor.kv-markdown-input {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #ffffff;
  padding: 10px;
  overflow-y: auto;
  width: 100%;

  &.kv-disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  &:focus-within {
    border-color: var(--primary, #0b9055);
    box-shadow: 0 0 0 2px rgba(11, 144, 85, 0.14);
  }

  .ProseMirror {
    color: #1f2a37;
    outline: none;
    min-height: calc(var(--editor-rows, 3) * 1.5em + 4px);
    max-height: calc(var(--editor-rows, 3) * 1.5em + 4px);
    overflow-y: auto;
    font-size: 14px;
    line-height: 1.5;
    font-family: inherit;

    * {
      margin: 0 !important;
    }

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      color: #9ca3af;
      pointer-events: none;
      float: left;
      height: 0;
    }
    
    .mention {
      font-family: inherit;
      background-color: #edfcf3;
      color: var(--primary, #0b9055);
      border-radius: 9999px;
      padding: 2px 6px 2px 2px;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      vertical-align: middle;
      font-weight: 500;
      font-size: 13px;
    }
  }
}
</style>
